import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useAppDispatch } from "../../hooks/useStore";
import { useSuperAdminAuth } from "../../hooks/useSuperAdminAuth";
import {
  loginSuperAdmin,
  clearError,
} from "../../store/slices/superAdminAuthSlice";
import { Input, FormField } from "../../components/UI/FormField";
import Button from "../../components/UI/Button";
import { Shield } from "lucide-react";
import { toast } from "react-toastify";
import type { SuperAdminLoginCredentials } from "../../types/superadmin";

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please provide a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
});

const SuperAdminLoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSuperAdminAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SuperAdminLoginCredentials>({
    resolver: yupResolver(loginSchema),
  });

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const onSubmit = async (data: SuperAdminLoginCredentials) => {
    try {
      await dispatch(loginSuperAdmin(data)).unwrap();
      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (err: any) {}
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F0E] py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Copper glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "-200px",
          right: "-200px",
          width: "500px",
          height: "500px",
          background: "#C97A3C",
          borderRadius: "50%",
          filter: "blur(200px)",
          opacity: 0.25,
        }}
      />
      <div className="max-w-md w-full relative z-10">
        <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center gap-3 mb-4">
              <svg width="36" height="36" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
                <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C"/>
                <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
              </svg>
            </div>
            <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E]">
              SuperAdmin Portal
            </h2>
            <p className="mt-2 text-sm text-[#8A9590] flex items-center justify-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-[#C97A3C]" />
              Platform Administration Access
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }}
            className="space-y-6"
          >
            <FormField
              label="Email Address"
              required
              error={errors.email?.message}
            >
              <Input
                {...register("email")}
                type="email"
                autoComplete="email"
                placeholder="admin@example.com"
                error={!!errors.email}
              />
            </FormField>

            <FormField
              label="Password"
              required
              error={errors.password?.message}
            >
              <Input
                {...register("password")}
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                error={!!errors.password}
              />
            </FormField>

            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-full"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-[#8A9590]">
              Authorized personnel only. All activity is monitored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLoginPage;
